const FALLBACK_LOCALE = "en-US";

/**
 * Turns a raw locale string into a BCP 47 tag that `Intl` accepts.
 *
 * `navigator.language` is not always a valid tag. Chromium on a Linux machine
 * with a POSIX locale reports forms like `en-US@posix`, `en_US.UTF-8` or `C`,
 * and `new Intl.DateTimeFormat(...)` throws a RangeError on each of them. The
 * `@modifier` and `.encoding` parts are dropped and `_` becomes `-`, so the
 * user's language survives when it can; anything still invalid falls back to
 * `en-US`.
 */
export function toIntlLocale(raw: string | null | undefined): string {
  const candidates = [raw, raw?.split(/[@.]/)[0]?.replace(/_/g, "-")];
  for (const candidate of candidates) {
    if (!candidate) continue;
    try {
      return Intl.getCanonicalLocales(candidate)[0] ?? FALLBACK_LOCALE;
    } catch {
      // Not a valid BCP 47 tag; try the next form.
    }
  }
  return FALLBACK_LOCALE;
}

/** The browser's locale as a tag `Intl` accepts (`en-US` during SSR). */
export function getBrowserLocale(): string {
  if (typeof window === "undefined") return FALLBACK_LOCALE;
  return toIntlLocale(window.navigator.language);
}
